import Typography from 'components/atoms/_layouts/Typography'
import * as React from 'react'
import 'react-dates/initialize'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../core/i18n'
import { IJsonResponse } from '../../../core/JsonApiSerializer'
import { reservationService, tourService } from '../../../core/services'
import * as constants from '../../../static/constants'
import * as interfaces from '../../../static/interfaces'
import { Chat } from '../reservations/parts'
import PreAssignForm from './guide_search/PreAssignForm'
import SidebarLayout from '../_layouts/sidebarLayout'
import { Panel, StatusBadge } from 'components/atoms'
import Avatar from '../../atoms/HeaderAvatar'
import moment from 'moment'
import { getTourGuideStatusLabel } from 'utils'
import SvgIcon from 'components/atoms/SvgIcon'

interface IMembersProps {
  guide: interfaces.IUser
  agent: interfaces.IUser
}

const Members: React.FC<IMembersProps> = props => {
  return (
    <S.Members>
      <h2>{I18n.t('generic.members')}</h2>
      <div className="AvatarWrap">
        <Avatar user={props.agent} />
      </div>
      <div className="AvatarWrap">
        <Avatar user={props.guide} />
      </div>
    </S.Members>
  )
}

interface IProps {
  agent: IJsonResponse
  guide: IJsonResponse
  user: IJsonResponse
  posts: IJsonResponse
  conversation: any
  selected_tour?: any
  post_id?: number
  day?: string
  start_time?: string
}

const PreAssignConversation: React.FC<IProps> = props => {
  const { data: guide } = reservationService.getDataFromJson(props.guide)
  const { data: agent } = reservationService.getDataFromJson(props.agent)
  const { data: user } = reservationService.getDataFromJson(props.user)
  const posts = user.agent ? reservationService.getDataFromJson(props.posts).data : []

  const defaultTour = React.useMemo(() => {
    const tour = props.selected_tour
      ? reservationService.getDataFromJson(props.selected_tour).data
      : null
    if (tour && posts.length > 0) {
      const post = posts.find(({ id }) => id === tour.post_id)
      return { ...tour, post }
    }
    return tour
  }, [props.selected_tour, posts])
  const { conversation } = props
  const [tourGuide, setTourGuide] = React.useState(null)
  const [selectedTour, setSelectedTour] = React.useState(
    defaultTour ? { label: defaultTour.post.name, value: defaultTour.post.id } : null
  )
  const [selectedDate, setSelectedDate] = React.useState(
    defaultTour ? moment(defaultTour.day) : moment()
  )
  const [selectedStartTime, setSelectedStartTime] = React.useState(
    defaultTour
      ? { value: defaultTour.start_time, label: defaultTour.start_time }
      : { value: '', label: '' }
  )
  const [messageData, setMessageData] = React.useState<{
    messages: interfaces.IMessage[]
    conversationId: number
  }>({
    messages: [],
    conversationId: conversation.id,
  })

  const getTourAndTourGuide = async (post_id, day, start_time) => {
    const { tour }: { tour: interfaces.ITours } = await tourService.searchTour({
      tour: { post_id, day, start_time },
    })
    if (tour) {
      const { tourGuides } = await tourService.getTourGuides(tour.id)
      const foundGuide = tourGuides.find(({ guide_id }) => guide_id === guide.id)
      setTourGuide(foundGuide)
    } else {
      setTourGuide(undefined)
    }
  }

  // チャットを取得する
  const getMessages = async () => {
    const conversationId = conversation.id
    if (conversationId) {
      const { data: newMessages } = await reservationService.getReservationMessages({
        conversation_id: conversationId,
      })
      const { data: parsedMessages } = reservationService.getDataFromJson(newMessages)
      setMessageData({
        messages: parsedMessages,
        conversationId,
      })
    }
  }

  React.useEffect(() => {
    getMessages()
  }, [])

  React.useEffect(() => {
    if (user.agent && selectedTour?.value && selectedDate && selectedStartTime?.value) {
      getTourAndTourGuide(
        selectedTour.value,
        selectedDate.format(constants.DATE_FORMAT),
        selectedStartTime.value
      )
    }
  }, [selectedTour, selectedDate, selectedStartTime])

  const preAssignFormProps = React.useMemo(
    () => ({
      posts,
      conversationId: messageData.conversationId,
      user,
      selectedTour,
      selectedDate,
      selectedStartTime,
      getMessages,
      setSelectedTour,
      setSelectedDate,
      setSelectedStartTime,
    }),
    [posts, messageData, user, selectedTour, selectedDate, selectedStartTime]
  )

  const badgeStatusFromStatus = React.useMemo(() => {
    const status = tourGuide?.request_status
    if (
      !status ||
      status === interfaces.TourGuideRequestStatus.nothing ||
      status === interfaces.TourGuideRequestStatus.agent_canceled
    ) {
      return 'empty'
    }
    if (status === interfaces.TourGuideRequestStatus.agent_pre_requested) {
      return 'usually'
    }
    if (status === interfaces.TourGuideRequestStatus.guide_approved) {
      return 'success'
    }
    if (status === interfaces.TourGuideRequestStatus.guide_declined) {
      return 'caution'
    }
    return 'info'
  }, [tourGuide])

  return (
    <SidebarLayout user={user}>
      <Panel noDecoration={user.guest ? true : false}>
        <Conversation>
          <div className="Conversation_Header">
            <a href="#" onClick={() => window.history.go(-1)}>
              <SvgIcon
                name="keyboard_arrow_left"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
              <span>{I18n.t('reservation.back_to_index')}</span>
            </a>
            <Typography type="headline" size="md">
              {I18n.t('conversation.sound_out_chat')}
            </Typography>
          </div>
          <div className="Conversation_Main">
            <div>
              <div className="Conversation_Form">
                {user.agent && <PreAssignForm {...preAssignFormProps} />}
                {user.agent && (
                  <StatusBadge
                    label={getTourGuideStatusLabel(tourGuide?.request_status)}
                    type={badgeStatusFromStatus}
                  />
                )}
              </div>
              <Members guide={guide} agent={agent} />
            </div>
            <div className="Conversation_Messages">
              {messageData.conversationId > 0 && (
                <Chat
                  user={user}
                  initMessages={messageData.messages}
                  conversationId={messageData.conversationId}
                />
              )}
            </div>
          </div>
        </Conversation>
      </Panel>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}
S.Members = styled.div`
  width: 240px;
  margin-bottom: 12px;
  flex-shrink: 1;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    width: 100%;
  }

  > h2 {
    font-size: 18px;
    font-weight: 700;
    color: ${constants.COLORS.subText};
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      font-size: 16px;
    }
    padding-bottom: 1rem;
    border-bottom: 1px solid ${constants.COLORS.divider};
  }

  .AvatarWrap {
    padding: 1em 0 1em 0;
    border-bottom: 1px solid ${constants.COLORS.divider};
    display: flex;
    align-items: center;
    justify-content: space-between;
    a {
      color: ${constants.COLORS.black};
    }
  }
`

const Conversation = styled.div`
  max-width: 1088px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    padding: 16px 12px;
  }

  .Conversation_Header {
    padding: 12px 0;

    > a {
      display: flex;
      align-items: center;
      color: ${constants.COLORS.primary};
      font-size: 14px;
      margin-bottom: 1em;
    }

    > h1 {
      text-align: center;
      font-size: 18px;
      margin-bottom: 24px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        font-size: 16px;
        margin-bottom: 12px;
      }
    }

    > h2 {
      text-align: center;
      font-size: 18px;
      margin-bottom: 24px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        font-size: 16px;
        margin-bottom: 12px;
      }
    }
  }

  .Conversation_Main {
    display: flex;
    gap: 32px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      display: block;
    }
  }
  .Conversation_Form {
    padding-bottom: 12px;
  }
  .Conversation_Messages {
    flex: 1;
    margin-bottom: 12px;
  }
`

export default PreAssignConversation
